// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — dimension descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Linear / aligned / angular / radial / ordinate dimensions. Every
// descriptor in this file targets the single Zod variant
// `geometry/create-dimension` (see `packages/contracts/src/editor.ts:341`),
// distinguished at runtime by the `dimensionType` integer field that maps
// onto the AutoCAD DXF group-72 dimension type code:
//   0 — rotated / linear (DIMENSION, DIMLINEAR)
//   1 — aligned          (DIMALIGNED)
//   2 — angular 2-line   (DIMANGULAR — between two lines)
//   3 — diameter         (DIMDIAMETER)
//   4 — radius           (DIMRADIUS)
//   5 — angular 3-point  (DIM3POINT — vertex + two arms)
//   6 — ordinate         (DIMORDINATE)
//
// We intentionally re-list `geometry/create-dimension` in every descriptor's
// `commandTypeLiterals` rather than authoring a single multi-id descriptor —
// AI / docs / ribbon need to address each AutoCAD command individually,
// even though the reducer dispatch is shared.
//
// All ID-text and prompt keys cross-checked against:
//   • `apps/web/src/app/scene/draftingHelpers.ts:410-433` (alias switch)
//   • `apps/web/src/app/scene/draftingHelpers.ts:549-555` (initial-step map)
//   • `apps/web/src/app/i18n/messages.ts:55-61, 132-151` (RU labels/prompts)
//   • `apps/web/src/app/i18n/messages.ts:2585-2591, 2658-2678` (EN copies)
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor, ParameterDescriptor } from "../types.js";

const REDUCER_FILE = "apps/web/src/app/state/geometryReducer.ts";
const CREATE_DIMENSION_LITERAL = "geometry/create-dimension";

// Common parameter set shared by every dimension descriptor — every
// dimension command produces the same Zod variant, so the parameter shape
// is identical and only the `dimensionType` integer narrows the variant.
const SHARED_DIMENSION_PARAMETERS: readonly ParameterDescriptor[] = [
  {
    name: "spaceId",
    type: "string",
    required: true,
    description: {
      ru: "Идентификатор пространства (Model или Layout), куда вставляется размер.",
      en: "Identifier of the target space (Model or Layout) the dimension belongs to.",
    },
  },
  {
    name: "extLine1Point",
    type: "point2d",
    required: true,
    description: {
      ru: "Начало первой выносной линии (или первая точка измерения).",
      en: "Origin of the first extension line (or first measurement point).",
    },
  },
  {
    name: "extLine2Point",
    type: "point2d",
    required: true,
    description: {
      ru: "Начало второй выносной линии (или вторая точка измерения).",
      en: "Origin of the second extension line (or second measurement point).",
    },
  },
  {
    name: "dimensionLinePoint",
    type: "point2d",
    required: true,
    description: {
      ru: "Положение размерной линии — точка, через которую проводится размерная линия или дуга.",
      en: "Dimension line location — point through which the dimension line or arc passes.",
    },
  },
  {
    name: "thirdPoint",
    type: "point3d",
    required: false,
    description: {
      ru: "Дополнительная вершина для 3-точечного углового размера (DIM3POINT, dimensionType=5). Только для DIM3POINT.",
      en: "Additional vertex for 3-point angular dimensions (DIM3POINT, dimensionType=5). Only used by DIM3POINT.",
    },
  },
  {
    name: "dimensionType",
    type: "enum",
    enumValues: ["0", "1", "2", "3", "4", "5", "6"],
    required: true,
    defaultValue: "0",
    description: {
      ru: "Тип размера по DXF group code 72: 0 линейный, 1 параллельный, 2 угловой, 3 диаметр, 4 радиус, 5 3-точечный угловой, 6 ординатный.",
      en: "Dimension type per DXF group code 72: 0 rotated, 1 aligned, 2 angular, 3 diameter, 4 radius, 5 3-point angular, 6 ordinate.",
    },
  },
  {
    name: "dimensionText",
    type: "string",
    required: false,
    defaultValue: "",
    description: {
      ru: "Переопределение текста размера. Пустая строка означает измеренное значение.",
      en: "Dimension text override. Empty string defers to the measured value.",
    },
  },
];

const REDUCER_LOCATIONS = [
  { commandTypeLiteral: CREATE_DIMENSION_LITERAL, file: REDUCER_FILE },
] as const;

export const DIMENSION_DESCRIPTORS: readonly CommandDescriptor[] = [
  {
    id: "DIMENSION",
    category: "dimension",
    status: "implemented",
    aiCallable: true,
    aliases: {
      en: ["DIMENSION", "DIM", "DIMLINEAR", "DLI"],
      ru: ["РАЗМЕР"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMENSION",
    draftSteps: [
      {
        stepId: "dimension-first-point",
        promptKeyRu: "prompt.dimension.first",
        promptKeyEn: "prompt.dimension.first",
        expects: "point",
      },
      {
        stepId: "dimension-second-point",
        promptKeyRu: "prompt.dimension.second",
        promptKeyEn: "prompt.dimension.second",
        expects: "point",
      },
      {
        stepId: "dimension-line-point",
        promptKeyRu: "prompt.dimension.line",
        promptKeyEn: "prompt.dimension.line",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Создаёт линейный (rotated) размер по двум выносным точкам и положению размерной линии.",
        long: "DIMENSION (РАЗМЕР) — базовая команда линейного размера. Запрашивает начало первой и второй выносных линий, затем положение размерной линии. Размер всегда строится горизонтально или вертикально в зависимости от направления, заданного третьей точкой. Алиас DIMLINEAR (DLI) совпадает с этой командой.",
        examples: ["DIMENSION", "DIM", "DLI"],
        seeAlso: ["DIMALIGNED", "DIMANGULAR", "DIMRADIUS", "DIMDIAMETER"],
      },
      en: {
        short:
          "Creates a linear (rotated) dimension between two extension points and a dimension line location.",
        long: "DIMENSION is the base linear-dimension command. It asks for the first and second extension-line origins, then for the dimension line location. The dimension snaps to horizontal or vertical depending on the direction implied by the third point. The DIMLINEAR (DLI) alias is identical.",
        examples: ["DIMENSION", "DIM", "DLI"],
        seeAlso: ["DIMALIGNED", "DIMANGULAR", "DIMRADIUS", "DIMDIAMETER"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimension.label",
      labelKeyEn: "command.dimension.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMALIGNED",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMALIGNED", "DAL"],
      ru: ["РЗМПАРАЛ", "РАЗМПАРАЛ"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMALIGNED",
    draftSteps: [
      {
        stepId: "dimaligned-first-point",
        promptKeyRu: "prompt.dimaligned.first",
        promptKeyEn: "prompt.dimaligned.first",
        expects: "point",
      },
      {
        stepId: "dimaligned-second-point",
        promptKeyRu: "prompt.dimaligned.second",
        promptKeyEn: "prompt.dimaligned.second",
        expects: "point",
      },
      {
        stepId: "dimaligned-line-point",
        promptKeyRu: "prompt.dimaligned.line",
        promptKeyEn: "prompt.dimaligned.line",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Параллельный размер — размерная линия выравнивается по линии между двумя выносными точками.",
        long: "DIMALIGNED (РЗМПАРАЛ) строит размер вдоль прямой, соединяющей две выносные точки, в отличие от DIMENSION который ортогонализирует. Удобен для измерения наклонных рёбер. Алиас DAL — стандартный AutoCAD shortcut.",
        examples: ["DIMALIGNED", "DAL", "РЗМПАРАЛ"],
        seeAlso: ["DIMENSION", "DIMANGULAR"],
      },
      en: {
        short:
          "Aligned dimension — the dimension line is parallel to the line between the two extension points.",
        long: "DIMALIGNED draws the dimension along the line connecting the two extension origins, unlike DIMENSION which snaps to ortho. Useful for measuring slanted edges. The DAL shortcut is the standard AutoCAD alias.",
        examples: ["DIMALIGNED", "DAL"],
        seeAlso: ["DIMENSION", "DIMANGULAR"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimaligned.label",
      labelKeyEn: "command.dimaligned.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMANGULAR",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMANGULAR", "DAN"],
      ru: ["РЗМУГЛОВ", "РАЗМУГЛ"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMANGULAR",
    draftSteps: [
      {
        stepId: "dimangular-first-line",
        promptKeyRu: "prompt.dimangular.first",
        promptKeyEn: "prompt.dimangular.first",
        expects: "selection",
      },
      {
        stepId: "dimangular-second-line",
        promptKeyRu: "prompt.dimangular.second",
        promptKeyEn: "prompt.dimangular.second",
        expects: "selection",
      },
      {
        stepId: "dimangular-arc-point",
        promptKeyRu: "prompt.dimangular.arc",
        promptKeyEn: "prompt.dimangular.arc",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Угловой размер между двумя выбранными отрезками. Положение дуги указывается курсором.",
        long: "DIMANGULAR (РЗМУГЛОВ) измеряет угол между двумя выбранными линиями. Сначала выбираются два отрезка, затем курсором задаётся положение размерной дуги — оно определяет, какой из четырёх возможных углов будет помечен. Аналог AutoCAD DAN.",
        examples: ["DIMANGULAR", "DAN"],
        seeAlso: ["DIM3POINT", "DIMENSION"],
      },
      en: {
        short:
          "Angular dimension between two selected lines. Arc location is picked with the cursor.",
        long: "DIMANGULAR measures the angle between two selected line segments. After picking the two lines the user specifies the dimension arc location, which decides which of the four possible angles is annotated. DAN is the standard AutoCAD shortcut.",
        examples: ["DIMANGULAR", "DAN"],
        seeAlso: ["DIM3POINT", "DIMENSION"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimangular.label",
      labelKeyEn: "command.dimangular.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIM3POINT",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIM3POINT", "DIMANGULAR3PT", "DAN3", "DI3", "DIM3"],
      ru: ["РЗМ3ТОЧКИ"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIM3POINT",
    draftSteps: [
      {
        stepId: "dim3point-vertex",
        promptKeyRu: "prompt.dim3point.vertex",
        promptKeyEn: "prompt.dim3point.vertex",
        expects: "point",
      },
      {
        stepId: "dim3point-first-arm",
        promptKeyRu: "prompt.dim3point.first",
        promptKeyEn: "prompt.dim3point.first",
        expects: "point",
      },
      {
        stepId: "dim3point-second-arm",
        promptKeyRu: "prompt.dim3point.second",
        promptKeyEn: "prompt.dim3point.second",
        expects: "point",
      },
      {
        stepId: "dim3point-arc-point",
        promptKeyRu: "prompt.dim3point.arc",
        promptKeyEn: "prompt.dim3point.arc",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Угловой размер по трём точкам: вершина угла плюс концы двух сторон.",
        long: "DIM3POINT (РЗМ3ТОЧКИ) — угловой размер заданный тремя точками вместо двух отрезков. Сначала указывается вершина угла, затем конец первой и конец второй стороны, и наконец положение размерной дуги. Используется когда углов нет в виде сегментов LINE/PLINE — например при простановке между воображаемыми осями. Группа кода DXF 72 равна 5; thirdPoint содержит вершину.",
        examples: ["DIM3POINT", "DAN3", "DI3"],
        seeAlso: ["DIMANGULAR", "DIMENSION"],
      },
      en: {
        short:
          "Angular dimension defined by three points: angle vertex plus the two arm endpoints.",
        long: "DIM3POINT is an angular dimension defined by three points instead of two segments. The user picks the angle vertex, then the endpoint of the first arm, the endpoint of the second arm, and finally the dimension arc location. Useful when there are no LINE/PLINE segments to select — for example when annotating between imaginary axes. DXF group code 72 is 5 and the thirdPoint field carries the vertex.",
        examples: ["DIM3POINT", "DAN3", "DI3"],
        seeAlso: ["DIMANGULAR", "DIMENSION"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dim3point.label",
      labelKeyEn: "command.dim3point.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMRADIUS",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMRADIUS", "DRA"],
      ru: ["РЗМРАДИУС"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMRADIUS",
    draftSteps: [
      {
        stepId: "dimradius-select-arc",
        promptKeyRu: "prompt.dimradius.select",
        promptKeyEn: "prompt.dimradius.select",
        expects: "selection",
      },
      {
        stepId: "dimradius-line-point",
        promptKeyRu: "prompt.dimradius.line",
        promptKeyEn: "prompt.dimradius.line",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Размер радиуса для выбранной дуги или окружности с лидером и префиксом R.",
        long: "DIMRADIUS (РЗМРАДИУС) проставляет радиус выбранной дуги или окружности. Текст размера автоматически получает префикс R. После выбора объекта пользователь курсором задаёт положение размерной линии. DXF group code 72 равен 4. Алиас DRA — стандартный AutoCAD shortcut.",
        examples: ["DIMRADIUS", "DRA"],
        seeAlso: ["DIMDIAMETER", "DIMENSION"],
      },
      en: {
        short:
          "Radius dimension for a selected arc or circle, drawn with leader and an R prefix.",
        long: "DIMRADIUS annotates the radius of a selected arc or circle. The dimension text is automatically prefixed with R. After selecting the entity the user picks the dimension line location with the cursor. DXF group code 72 is 4. DRA is the standard AutoCAD shortcut.",
        examples: ["DIMRADIUS", "DRA"],
        seeAlso: ["DIMDIAMETER", "DIMENSION"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimradius.label",
      labelKeyEn: "command.dimradius.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMDIAMETER",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMDIAMETER", "DDI"],
      ru: ["РЗМДИАМЕТР"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMDIAMETER",
    draftSteps: [
      {
        stepId: "dimdiameter-select-arc",
        promptKeyRu: "prompt.dimdiameter.select",
        promptKeyEn: "prompt.dimdiameter.select",
        expects: "selection",
      },
      {
        stepId: "dimdiameter-line-point",
        promptKeyRu: "prompt.dimdiameter.line",
        promptKeyEn: "prompt.dimdiameter.line",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Размер диаметра для выбранной дуги или окружности с префиксом ⌀.",
        long: "DIMDIAMETER (РЗМДИАМЕТР) проставляет диаметр выбранной дуги или окружности. Текст автоматически получает префикс знака диаметра. Похож на DIMRADIUS, но размерная линия проводится через центр окружности и измеряет полное расстояние через центр. DXF group code 72 равен 3. Алиас DDI — стандартный AutoCAD shortcut.",
        examples: ["DIMDIAMETER", "DDI"],
        seeAlso: ["DIMRADIUS", "DIMENSION"],
      },
      en: {
        short:
          "Diameter dimension for a selected arc or circle, drawn with a diameter prefix.",
        long: "DIMDIAMETER annotates the diameter of a selected arc or circle. The dimension text is automatically prefixed with the diameter symbol. Similar to DIMRADIUS but the dimension line passes through the centre and measures the full diameter. DXF group code 72 is 3. DDI is the standard AutoCAD shortcut.",
        examples: ["DIMDIAMETER", "DDI"],
        seeAlso: ["DIMRADIUS", "DIMENSION"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimdiameter.label",
      labelKeyEn: "command.dimdiameter.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMORDINATE",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMORDINATE", "DOR"],
      ru: ["РЗМОРДИНАТА"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMORDINATE",
    draftSteps: [
      {
        stepId: "dimordinate-feature-point",
        promptKeyRu: "prompt.dimordinate.feature",
        promptKeyEn: "prompt.dimordinate.feature",
        expects: "point",
      },
      {
        stepId: "dimordinate-leader-endpoint",
        promptKeyRu: "prompt.dimordinate.leader",
        promptKeyEn: "prompt.dimordinate.leader",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Ординатный размер — координата X или Y точки относительно текущей UCS.",
        long: "DIMORDINATE (РЗМОРДИНАТА) проставляет ординату — расстояние по X или Y от начала текущей UCS до выбранной точки элемента. Направление лидера определяет, какая координата (X или Y) будет показана: вертикальный лидер → X-ордината, горизонтальный → Y-ордината. DXF group code 72 равен 6. Алиас DOR — стандартный AutoCAD shortcut.",
        examples: ["DIMORDINATE", "DOR"],
        seeAlso: ["DIMENSION", "DIMALIGNED"],
      },
      en: {
        short:
          "Ordinate dimension — the X or Y coordinate of a feature point relative to the current UCS.",
        long: "DIMORDINATE annotates the ordinate — the X or Y distance from the current UCS origin to a selected feature point. The leader direction determines which coordinate is shown: a vertical leader yields the X-ordinate, a horizontal leader yields the Y-ordinate. DXF group code 72 is 6. DOR is the standard AutoCAD shortcut.",
        examples: ["DIMORDINATE", "DOR"],
        seeAlso: ["DIMENSION", "DIMALIGNED"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimordinate.label",
      labelKeyEn: "command.dimordinate.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMBASELINE",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMBASELINE", "DBA"],
      ru: ["РЗМБАЗ", "РАЗМБАЗ"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMBASELINE",
    draftSteps: [
      {
        stepId: "dimbaseline-second-point",
        promptKeyRu: "prompt.dimbaseline.second",
        promptKeyEn: "prompt.dimbaseline.second",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Цепочка размеров с общей базовой выносной линией от последнего линейного размера.",
        long: "DIMBASELINE (РЗМБАЗ) продолжает цепочку линейных или параллельных размеров от первой выносной линии последнего созданного размера. Каждый клик задаёт вторую выносную точку нового размера; первая берётся из предыдущего. DXF group code 72 совпадает с базовым размером (0 для линейного, 1 для параллельного). Алиас DBA — стандартный AutoCAD shortcut.",
        examples: ["DIMBASELINE", "DBA"],
        seeAlso: ["DIMENSION", "DIMCONTINUE"],
      },
      en: {
        short:
          "Chain of dimensions sharing the first extension line of the last linear/aligned dimension.",
        long: "DIMBASELINE continues a chain of linear/aligned dimensions from the first extension line of the most recently created dimension. Each click specifies the second extension origin of a new dimension; the first is reused from the predecessor. DXF group code 72 matches the base dimension (0 linear, 1 aligned). DBA is the standard AutoCAD shortcut.",
        examples: ["DIMBASELINE", "DBA"],
        seeAlso: ["DIMENSION", "DIMCONTINUE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimbaseline.label",
      labelKeyEn: "command.dimbaseline.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
  {
    id: "DIMCONTINUE",
    category: "dimension",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 (only generic DIM); AI deferred to CC-2c + §17 ТЗ revision
    aiCallable: false,
    aliases: {
      en: ["DIMCONTINUE", "DCO"],
      ru: ["РЗМЦЕПЬ", "РАЗМЦЕПЬ"],
    },
    commandTypeLiterals: [CREATE_DIMENSION_LITERAL],
    draftCommandId: "DIMCONTINUE",
    draftSteps: [
      {
        stepId: "dimcontinue-second-point",
        promptKeyRu: "prompt.dimcontinue.second",
        promptKeyEn: "prompt.dimcontinue.second",
        expects: "point",
      },
    ],
    parameters: SHARED_DIMENSION_PARAMETERS,
    help: {
      ru: {
        short:
          "Цепочка размеров: новая выносная линия начинается от второй выносной линии последнего размера.",
        long: "DIMCONTINUE (РЗМЦЕПЬ) продолжает цепочку линейных или параллельных размеров: первая выносная линия нового размера совпадает со второй выносной линией предыдущего, а пользователь кликом задаёт новую вторую выносную точку. Размерная линия выравнивается с предыдущим размером. DXF group code 72 совпадает с базовым размером. Алиас DCO — стандартный AutoCAD shortcut.",
        examples: ["DIMCONTINUE", "DCO"],
        seeAlso: ["DIMENSION", "DIMBASELINE"],
      },
      en: {
        short:
          "Chained dimensions: each new dimension starts at the second extension origin of the previous.",
        long: "DIMCONTINUE chains linear/aligned dimensions end-to-end: the first extension origin of the new dimension is the second extension origin of the previous, and the user clicks the new second extension origin. The dimension line aligns with the prior dimension. DXF group code 72 matches the base. DCO is the standard AutoCAD shortcut.",
        examples: ["DIMCONTINUE", "DCO"],
        seeAlso: ["DIMENSION", "DIMBASELINE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.dimcontinue.label",
      labelKeyEn: "command.dimcontinue.label",
      ribbonTab: "annotate",
      ribbonSection: "dimensions",
      glyph: null,
    },
    reducerLocations: REDUCER_LOCATIONS,
  },
];
